import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import * as yup from "yup";

import { useUpdateTicketSizeForDealOpportunityGp } from "@vega-api/generated/coinvest-deal-opportunity-gp-actions/coinvest-deal-opportunity-gp-actions";
import { getGetDealTrackingForGpQueryKey } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import { type Currency as CurrencyType } from "@vega-api/generated/types/currency";
import { MoneyField } from "@vega-core/components/Form/Fields/MoneyField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { Notice } from "@vega-core/components/Notice/Notice";
import { useToast } from "@vega-core/components/Toast/useToast";
import { type CoreMoney } from "@vega-core/models/money";
import { m } from "@vega-core/paraglide/messages";
import { useDefaultCurrency } from "@vega-core/utils/formatMoney";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

import { toMoneyPayload } from "../utils/moneyUtils";

import { ModalButtonGroup } from "./ModalButtonGroup";

type EditMaxTicketSizeModalProps = {
  dealOpportunityId: string;
  dealId: string;
  dealCurrency?: CurrencyType;
  isOpen: boolean;
  onClose: () => void;
  initialValues?: {
    minimum?: CoreMoney;
    maximum?: CoreMoney;
  };
};

type FormValues = {
  amount: CoreMoney;
};

const createValidationSchema = (minimumAmount?: number) =>
  yup.object({
    amount: yup.object({
      amount: yup
        .number()
        .required(m.coInvestments_submitTicketSize_modal_maxInputRequired())
        .min(
          minimumAmount ?? 0,
          m.coInvestments_submitTicketSize_modal_maxInputMustBeAtLeastMin(),
        ),
      currency: yup.string().required(),
    }),
  });

export const EditMaxTicketSizeModal = ({
  dealOpportunityId,
  dealId,
  dealCurrency,
  isOpen,
  onClose,
  initialValues,
}: EditMaxTicketSizeModalProps) => {
  const defaultCurrency = useDefaultCurrency();
  const currency = dealCurrency ?? defaultCurrency;
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const { mutate: updateTicketSize, isPending } =
    useUpdateTicketSizeForDealOpportunityGp({
      mutation: {
        onSuccess: () => {
          showToast({
            variant: "success",
            title: m.coInvestments_submitTicketSize_toast_success(),
          });
          queryClient.invalidateQueries({
            queryKey: getGetDealTrackingForGpQueryKey(dealId),
          });
          onClose();
        },
        onError: (error) => {
          showErrorToast(error, {
            defaultMessage: m.coInvestments_submitTicketSize_toast_error(),
          });
        },
      },
    });

  const getInitialValues = (): FormValues => ({
    amount: initialValues?.maximum ?? {
      amount: 0,
      currency,
    },
  });

  const handleSubmit = (values: FormValues) => {
    const maximum = toMoneyPayload(values.amount);

    if (!maximum) return;

    // If no minimum is provided, use 0 as the minimum
    const minimum = toMoneyPayload(initialValues?.minimum) || {
      amount: 0,
      currency,
    };

    updateTicketSize({
      dealOpportunityId,
      data: {
        minimum,
        maximum,
      },
    });
  };

  return (
    <ActionaryModal
      dataTestId="edit-max-ticket-size-modal"
      fluidHeight
      open={isOpen}
      setOpen={onClose}
      title={m.coInvestments_submitTicketSize_modal_titleMaximum()}
    >
      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        onSubmit={handleSubmit}
        validationSchema={createValidationSchema(
          initialValues?.minimum?.amount,
        )}
      >
        <Form className="space-y-6">
          <MoneyField
            allowAbbreviations
            currency={currency}
            isRequired
            label={m.coInvestments_submitTicketSize_modal_maxInputLabel()}
            name="amount"
          />

          <Notice
            className="text-left"
            content={m.coInvestments_lpVisibilityWarning_description()}
            title={m.coInvestments_lpVisibilityWarning_title()}
            type="info"
            variant="accent"
          />

          <ModalButtonGroup
            cancelTestId="cancel-edit-max-ticket-size-button"
            confirmTestId="confirm-edit-max-ticket-size-button"
            isSubmitting={isPending}
            onCancel={onClose}
          />
        </Form>
      </Formik>
    </ActionaryModal>
  );
};
